import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { ListScreen, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Dialog } from '../components/ui/dialog'
import { Field, Input, Textarea } from '../components/ui/field'
import { Blank, Panel } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip } from '../components/ui/tone'
import {
  ApiError,
  api,
  type Page,
  type Template,
  type TemplateVersionFull,
  type VariableDefinition,
  type VariableValidation,
} from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Templates (§28, §64).
 *
 * The rule this screen has to teach, because it is unusual and people work around it otherwise:
 * **a version is never edited.** Changing the wording adds a version. The old one stays exactly as
 * it was, because every message that was sent points at the version it used, and a message sent
 * last March has to keep meaning what it meant in March.
 *
 * So the list shows the version number and how many versions exist, rather than hiding history
 * behind a single "last edited" date that would imply the previous wording is gone.
 *
 * Approval (§64) is shown separately from authoring, because they are separate acts by separate
 * people — `edit_templates` writes, `approve_messages` approves.
 *
 * **Until 2026-08-25 this screen could create a template and nothing else** — the container, with
 * an empty first version, and no way to write a word into it from the console. Wording had to be
 * `POST`ed by hand. `WordingDialog` is where a version is now written, checked against §116 item
 * 40's variable vocabulary as it is typed, and approved.
 */
export function TemplatesScreen() {
  const { can } = useSession()
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [openTemplate, setOpenTemplate] = useState<Template | null>(null)

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Page<Template>>('/templates?take=200'),
    enabled: can('view_contacts'),
  })

  if (!can('view_contacts')) {
    return (
      <Screen>
        <ScreenHeader title="Templates" description="The wording campaigns send." />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to templates"
            body="Ask an administrator of your organization."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = (templates.data?.items ?? []).filter(
    (template) =>
      !search ||
      template.name.toLowerCase().includes(search.toLowerCase()) ||
      (template.family ?? '').toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <>
      <ListScreen
        title="Templates"
        subtitle="Changing wording adds a version — it never rewrites one. Every message that was sent keeps pointing at the version it actually used (§28)."
        query={search}
        onQuery={setSearch}
        queryPlaceholder="Filter templates"
        primary={
          can('edit_templates') ? (
            <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
              New template
            </Button>
          ) : undefined
        }
      >
        {templates.isLoading ? (
          <TableSkeleton columns={6} />
        ) : templates.isError ? (
          <Blank
            icon="error"
            tone="bad"
            title="Templates could not be loaded"
            action={<Button onClick={() => void templates.refetch()}>Try again</Button>}
          />
        ) : rows.length > 0 ? (
          <Table>
            <THead>
              <Th>Name</Th>
              <Th>Latest subject</Th>
              <Th width={110}>Family</Th>
              <Th width={90} align="right">
                Version
              </Th>
              <Th width={140}>Approval</Th>
              <Th width={120}>Updated</Th>
            </THead>
            <TBody>
              {rows.map((template) => {
                const latest = template.versions[0]
                return (
                  <Tr key={template.id} onOpen={() => setOpenTemplate(template)}>
                    <Td>{template.name}</Td>
                    <Td className="text-muted">
                      {latest?.subjectTemplate ? (
                        latest.subjectTemplate
                      ) : (
                        // An empty first version is the normal state of a template nobody has
                        // written yet. Saying so beats an empty cell that reads as a bug.
                        <span className="text-faint">not written yet</span>
                      )}
                    </Td>
                    <Td className="text-muted">
                      {template.family ?? <span className="text-faint">—</span>}
                    </Td>
                    <Td align="right" className="tabular-nums">
                      {latest ? `v${latest.version}` : '—'}
                      {template._count.versions > 1 && (
                        <span className="text-faint"> of {template._count.versions}</span>
                      )}
                    </Td>
                    <Td>
                      {latest?.approvedAt ? (
                        <Chip tone="ok">approved</Chip>
                      ) : (
                        <Chip tone="warn">not approved</Chip>
                      )}
                    </Td>
                    <Td className="text-muted">
                      {new Date(template.updatedAt).toLocaleDateString()}
                    </Td>
                  </Tr>
                )
              })}
            </TBody>
          </Table>
        ) : search ? (
          <Blank
            icon="noResults"
            title={`No template matches “${search}”.`}
            action={<Button onClick={() => setSearch('')}>Clear filter</Button>}
          />
        ) : (
          <Blank
            icon="templates"
            title="No templates yet."
            body="A template holds the wording; each edit becomes a new version, and a message records which version it used. Variables are checked against what the dispatcher can actually supply as you write (§30, §116 item 40)."
            action={
              can('edit_templates') ? (
                <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                  New template
                </Button>
              ) : undefined
            }
          />
        )}
      </ListScreen>

      <CreateDialog open={creating} onClose={() => setCreating(false)} />
      <WordingDialog template={openTemplate} onClose={() => setOpenTemplate(null)} />
    </>
  )
}

/** `GET /templates/:id` — the same shape the list carries, with the bodies. */
interface TemplateDetail extends Template {
  versions: TemplateVersionFull[]
}

/**
 * Writing a version, and §116 item 40 at the point it is useful.
 *
 * Three things this dialog has to get right, and each one is a rule from somewhere else:
 *
 * - **It never edits.** §28. The existing wording is loaded into the editor as a starting point
 *   and saving creates the next version; the previous one is still there and still says what it
 *   said. The header says the version number that will be created, so nobody believes otherwise.
 * - **It checks as you type**, against `POST /templates/validate`, which writes nothing. A
 *   `BLOCK` disables the save; a `WARN` is shown and does not.
 * - **Approve is a different act by a different permission** (§64). It is offered on the latest
 *   saved version, not on the draft in the editor — approving text that has not been saved would
 *   approve something no message could ever point at.
 */
function WordingDialog({
  template,
  onClose,
}: {
  template: Template | null
  onClose: () => void
}) {
  const { can } = useSession()
  const queryClient = useQueryClient()

  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [footer, setFooter] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showVariables, setShowVariables] = useState(false)

  const detail = useQuery({
    queryKey: ['template', template?.id],
    queryFn: () => api.get<TemplateDetail>(`/templates/${template!.id}`),
    enabled: Boolean(template),
  })

  const latest = detail.data?.versions[0]

  // Load the latest wording into the editor once, when a template is opened. Not on every
  // `detail` change — that would throw away what the author had typed each time the query
  // refetched in the background.
  useEffect(() => {
    if (!template) return
    setError(null)
    setSubject(latest?.subjectTemplate ?? '')
    setText(latest?.textBody ?? '')
    setFooter(latest?.footer ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id, latest?.id])

  const variables = useQuery({
    queryKey: ['template-variables'],
    queryFn: () =>
      api.get<{ variables: VariableDefinition[]; note: string }>('/templates/variables'),
    // The vocabulary changes when the code changes, not when data does.
    staleTime: Infinity,
    enabled: Boolean(template),
  })

  // Debounced so a keystroke is not a request. 400ms is long enough to finish a word.
  const [debounced, setDebounced] = useState({ subject: '', text: '', footer: '' })
  useEffect(() => {
    const id = setTimeout(() => setDebounced({ subject, text, footer }), 400)
    return () => clearTimeout(id)
  }, [subject, text, footer])

  const validation = useQuery({
    queryKey: ['template-validate', debounced],
    queryFn: () =>
      api.post<VariableValidation>('/templates/validate', {
        subjectTemplate: debounced.subject || ' ',
        textBody: debounced.text || ' ',
        footer: debounced.footer || undefined,
      }),
    enabled: Boolean(template) && can('edit_templates'),
  })

  const save = useMutation({
    mutationFn: () =>
      api.post(`/templates/${template!.id}/versions`, {
        subjectTemplate: subject,
        textBody: text,
        footer: footer.trim() || undefined,
      }),
    onSuccess: async () => {
      setError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['templates'] }),
        queryClient.invalidateQueries({ queryKey: ['template', template?.id] }),
      ])
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'Could not save this version'),
  })

  const approve = useMutation({
    mutationFn: () => api.post(`/templates/versions/${latest!.id}/approve`, {}),
    onSuccess: async () => {
      setError(null)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['templates'] }),
        queryClient.invalidateQueries({ queryKey: ['template', template?.id] }),
      ])
    },
    onError: (e: Error) =>
      setError(e instanceof ApiError ? e.message : 'Could not approve this version'),
  })

  if (!template) return null

  const blocking = (validation.data?.findings ?? []).filter((f) => f.severity === 'BLOCK')
  const warnings = (validation.data?.findings ?? []).filter((f) => f.severity === 'WARN')
  const nextVersion = (latest?.version ?? 0) + 1

  const unchanged =
    subject === (latest?.subjectTemplate ?? '') &&
    text === (latest?.textBody ?? '') &&
    footer === (latest?.footer ?? '')

  const saveBlockedBecause = !can('edit_templates')
    ? 'You do not have permission to write templates'
    : subject.trim() === '' || text.trim() === ''
      ? 'A subject and a body are both required'
      : unchanged
        ? 'Nothing has changed — saving would create a version identical to the last one'
        : blocking.length > 0
          ? `${blocking[0]!.message}`
          : null

  return (
    <Dialog
      open
      width={720}
      title={template.name}
      description={`Saving creates version ${nextVersion}. Version ${latest?.version ?? 0} stays exactly as it is — every message that used it still points at it (§28).`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
          {latest && !latest.approvedAt && can('approve_messages') && (
            <Button
              disabled={approve.isPending || !latest.subjectTemplate}
              title={
                latest.subjectTemplate
                  ? undefined
                  : 'This version has no wording in it yet'
              }
              onClick={() => approve.mutate()}
            >
              {approve.isPending ? 'Approving…' : `Approve v${latest.version}`}
            </Button>
          )}
          <Button
            variant="primary"
            disabled={saveBlockedBecause !== null || save.isPending}
            // The reason is in the accessible name, not only in a tooltip — a disabled control
            // whose reason is invisible is one an operator reports as broken.
            title={saveBlockedBecause ?? undefined}
            aria-label={
              saveBlockedBecause
                ? `Save version ${nextVersion} — unavailable: ${saveBlockedBecause}`
                : `Save version ${nextVersion}`
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : `Save as v${nextVersion}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p role="alert" className="text-bad text-[12px]">
            {error}
          </p>
        )}

        <Field label="Subject" error={undefined}>
          {(props) => (
            <Input
              {...props}
              value={subject}
              placeholder="Call for papers — {{ journal.name }}"
              onChange={(e) => setSubject(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Body"
          hint="Plain text. Placeholders are written {{ contact.firstName }} — there are no conditionals or filters, deliberately (§29)."
        >
          {(props) => (
            <Textarea
              {...props}
              rows={12}
              value={text}
              placeholder={'Dear {{ contact.firstName }},\n\n…\n\n{{ sender.identity }}'}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Footer"
          hint="Optional, appended to the body. §31’s opt-out link belongs here: {{ unsubscribe.url }}"
        >
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={footer}
              placeholder="You are receiving this because… Opt out: {{ unsubscribe.url }}"
              onChange={(e) => setFooter(e.target.value)}
            />
          )}
        </Field>

        <div className="bg-line h-px w-full" />

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[12.5px]">Variables</span>
            <button
              type="button"
              className="text-muted hover:text-fg text-[11.5px]"
              onClick={() => setShowVariables((v) => !v)}
            >
              {showVariables ? 'hide the list' : 'what can I use?'}
            </button>
          </div>

          {validation.isError ? (
            <p className="text-muted text-[12px]">
              The variable check could not run. Saving is still possible — §30 would refuse a
              broken message at send time rather than delivering it.
            </p>
          ) : blocking.length === 0 && warnings.length === 0 ? (
            <p className="text-muted text-[12px]">
              {validation.isFetching
                ? 'Checking…'
                : 'Every variable in this draft resolves to something the dispatcher can supply.'}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {[...blocking, ...warnings].map((finding) => (
                <li key={finding.code + finding.message} className="flex gap-2 text-[12px]">
                  <Chip tone={finding.severity === 'BLOCK' ? 'bad' : 'warn'}>
                    {finding.severity === 'BLOCK' ? 'blocks' : 'warns'}
                  </Chip>
                  <span className="text-muted min-w-0">{finding.message}</span>
                </li>
              ))}
            </ul>
          )}

          {showVariables && (
            <div className="space-y-1 pt-1">
              {(variables.data?.variables ?? []).map((variable) => (
                <div key={variable.name} className="flex gap-2 text-[12px]">
                  <code className="text-fg shrink-0 font-mono text-[11.5px]">
                    {`{{ ${variable.name} }}`}
                  </code>
                  <span className="text-faint min-w-0">
                    {variable.description}
                    {variable.optional && ' — may be blank, which stops the send for that recipient'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  )
}

function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [family, setFamily] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post('/templates', { name, family: family.trim() || undefined }),
    onSuccess: async () => {
      setName('')
      setFamily('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
      onClose()
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : 'Could not create'),
  })

  return (
    <Dialog
      open={open}
      title="New template"
      description="This creates the container and an empty first version. The wording is written into a version, never into the template itself."
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={name.trim().length < 2 || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" error={error}>
          {(props) => (
            <Input
              {...props}
              value={name}
              placeholder="First approach — call for papers"
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Family"
          hint="Optional. Groups templates that are variants of the same message."
        >
          {(props) => (
            <Input
              {...props}
              value={family}
              placeholder="call-for-papers"
              onChange={(e) => setFamily(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
